import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTPageLayout from '../common/RTPageLayout';
import apiClient from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import UpdateComplaintStatusModal from './UpdateComplaintStatusModal';
import AssignComplaintModal from './AssignComplaintModal';
import tenantStorage from '../../utils/tenantStorage';
import './ViewComplaintDetails.css';
import '../common/rtCommon.css';

const ViewComplaintDetails = () => {
  const { complaintId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPgMode = location.pathname.startsWith('/pg/');
  const readOnly = isPgMode;

  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateAction, setUpdateAction] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignMode, setAssignMode] = useState(null);

  useEffect(() => {
    if (complaintId) {
      fetchComplaintDetails();
    }
  }, [complaintId, isPgMode]);

  const fetchComplaintDetails = async () => {
    setLoading(true);
    try {
      const url = isPgMode
        ? `/pg/me/complaints/${complaintId}`
        : `/complaints/${complaintId}`;
      const response = await apiClient.get(url);
      setComplaint(response.data);
    } catch (error) {
      console.error('Error fetching complaint details:', error);
      toastError(
        error.response?.data?.detail || 'Failed to fetch complaint details',
      );
      setComplaint(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleAssignComplaint = () => {
    setAssignMode('assign');
    setShowAssignModal(true);
  };

  const handleAssignToMyself = () => {
    // Set mode to assignSelf for the modal
    setAssignMode('assignSelf');
    setShowAssignModal(true);
  };

  const handleStartTicket = () => {
    setUpdateAction('start');
    setShowUpdateModal(true);
  };
  const handleResolveTicket = () => {
    setUpdateAction('resolve');
    setShowUpdateModal(true);
  };

  const handleAddNote = () => {
    setUpdateAction('addNote');
    setShowUpdateModal(true);
  };

  const handleCloseComplaint = () => {
    setUpdateAction('close');
    setShowUpdateModal(true);
  };

  const isRTAdmin = () => {
    const userRole = tenantStorage.getItem('userRole');
    return userRole === 'RT_Admin';
  };

  const isRTStaff = () => {
    const userRole = tenantStorage.getItem('userRole');
    return userRole === 'RT_Staff';
  };

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase().replace(/_/g, '-');
    return (
      <span className={`complaint-status status-${statusClass}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    return (
      <span className={`complaint-priority priority-${priority.toLowerCase()}`}>
        {priority}
      </span>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return '🔴';
      case 'assigned':
        return '👤';
      case 'in_progress':
        return '🟡';
      case 'resolved':
        return '🟢';
      case 'closed':
        return '⚫';
      default:
        return '❓';
    }
  };

  const getProgressSteps = () => {
    const steps = [
      { status: 'open', label: 'Open', description: 'Complaint submitted' },
      {
        status: 'assigned',
        label: 'Assigned',
        description: complaint?.assigned_to_name
          ? `Assigned to ${complaint.assigned_to_name}`
          : 'Assigned to staff',
      },
      {
        status: 'in_progress',
        label: 'In Progress',
        description: 'Being worked on',
      },
      { status: 'resolved', label: 'Resolved', description: 'Issue fixed' },
      { status: 'closed', label: 'Closed', description: 'Complaint closed' },
    ];

    const currentStatus = complaint?.status;
    const hasAssignedTo = complaint?.assigned_to;
    const statusOrder = [
      'open',
      'assigned',
      'in_progress',
      'resolved',
      'closed',
    ];

    // If complaint has assigned_to but status is still 'open', treat it as 'assigned'
    const effectiveStatus =
      currentStatus === 'open' && hasAssignedTo ? 'assigned' : currentStatus;
    const currentIndex = statusOrder.indexOf(effectiveStatus);

    return steps.map((step, index) => ({
      ...step,
      isActive: index <= currentIndex,
      isCurrent: step.status === effectiveStatus,
    }));
  };

  if (loading) {
    return (
      <RTPageLayout title="Complaint Details" mainSection={true}>
        <RTSpinner message="Loading complaint…" fullScreen={false} />
      </RTPageLayout>
    );
  }

  if (!complaint) {
    return (
      <RTPageLayout title="Complaint Details" mainSection={true}>
        <h4>Complaint not found</h4>
        <RTButton onClick={handleBack}>Back</RTButton>
      </RTPageLayout>
    );
  }

  const actionButtons = (
    <div className="complaint-action-buttons">
      {!readOnly && (
        <>
          {isRTAdmin() &&
            complaint.status === 'open' &&
            !complaint.assigned_to && (
              <RTButton
                variant="primary"
                permissionKey="complaints.add"
                onClick={handleAssignComplaint}
              >
                Assign Complaint
              </RTButton>
            )}
          {isRTStaff() &&
            complaint.status === 'open' &&
            !complaint.assigned_to && (
              <RTButton
                variant="success"
                permissionKey="complaints.add"
                onClick={handleAssignToMyself}
              >
                Assign to Myself
              </RTButton>
            )}
          {(complaint.status === 'assigned' ||
            (complaint.status === 'open' && complaint.assigned_to)) && (
            <RTButton
              variant="success"
              permissionKey="complaints.add"
              onClick={handleStartTicket}
            >
              Start Ticket
            </RTButton>
          )}
          {complaint.status === 'in_progress' && (
            <RTButton
              variant="warning"
              permissionKey="complaints.add"
              onClick={handleResolveTicket}
            >
              Resolve Ticket
            </RTButton>
          )}
          {isRTAdmin() &&
            (complaint.status === 'resolved' ||
              complaint.status === 'open') && (
              <RTButton
                variant="danger"
                permissionKey="complaints.add"
                onClick={handleCloseComplaint}
              >
                Close Complaint
              </RTButton>
            )}
          {(complaint.status === 'in_progress' ||
            complaint.status === 'resolved') && (
            <RTButton
              variant="info"
              permissionKey="complaints.add"
              onClick={handleAddNote}
            >
              Add Note
            </RTButton>
          )}
        </>
      )}
      <RTButton variant="outline" onClick={handleBack}>
        Back
      </RTButton>
    </div>
  );

  return (
    <>
      <RTPageLayout
        title={`Complaint Details #${complaint.complaint_id}`}
        actions={actionButtons}
        mainSection={true}
      >
        <div>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            {getStatusBadge(complaint.status)}
            {getPriorityBadge(complaint.priority)}
          </div>
          {/* Complaint Information in Two Columns */}
          <div className="rt-section" style={{ marginBottom: '30px' }}>
            <h3 className="rt-section-title">Complaint Information</h3>
            <div
              className="complaint-details-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '30px',
              }}
            >
              {/* Left Column */}
              <div className="complaint-details-form-table">
                <div className="complaint-details-form-row">
                  <div className="complaint-details-form-label">
                    Created Date
                  </div>
                  <div className="complaint-details-form-value">
                    {formatDateToDDMMYYYY(complaint.created_at)}
                  </div>
                </div>

                <div className="complaint-details-form-row">
                  <div className="complaint-details-form-label">Type</div>
                  <div className="complaint-details-form-value">
                    {complaint.complaint_type}
                  </div>
                </div>

                <div className="complaint-details-form-row">
                  <div className="complaint-details-form-label">
                    Paying Guest
                  </div>
                  <div className="complaint-details-form-value">
                    {readOnly ? (
                      complaint.pg_name
                    ) : (
                      <RTButton
                        type="link"
                        onClick={() =>
                          window.open(
                            `/paying-guest/view/${complaint.pg_id}`,
                            '_blank',
                          )
                        }
                        className="rt-link-button"
                      >
                        {complaint.pg_name}
                      </RTButton>
                    )}
                  </div>
                </div>

                <div className="complaint-details-form-row">
                  <div className="complaint-details-form-label">Building</div>
                  <div className="complaint-details-form-value">
                    {readOnly ? (
                      complaint.building_name
                    ) : (
                      <RTButton
                        type="link"
                        onClick={() =>
                          window.open(
                            `/building/view/${complaint.building_id}`,
                            '_blank',
                          )
                        }
                        className="rt-link-button"
                      >
                        {complaint.building_name}
                      </RTButton>
                    )}
                  </div>
                </div>

                <div className="complaint-details-form-row">
                  <div className="complaint-details-form-label">Room</div>
                  <div className="complaint-details-form-value">
                    {complaint.room_name || complaint.room_type || 'N/A'}
                  </div>
                </div>

                {complaint.resolved_at && (
                  <div className="complaint-details-form-row">
                    <div className="complaint-details-form-label">
                      Resolved Date
                    </div>
                    <div className="complaint-details-form-value">
                      {formatDateToDDMMYYYY(complaint.resolved_at)}
                    </div>
                  </div>
                )}

                {complaint.resolution_notes && (
                  <div className="complaint-details-form-row">
                    <div className="complaint-details-form-label">
                      Resolution Notes
                    </div>
                    <div className="complaint-details-form-value">
                      {complaint.resolution_notes}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="complaint-details-form-table">
                <div className="complaint-details-form-row">
                  <div className="complaint-details-form-label">
                    Assigned To
                  </div>
                  <div className="complaint-details-form-value">
                    {complaint.assigned_to
                      ? complaint.assigned_to_name
                      : 'Not assigned'}
                  </div>
                </div>

                <div className="complaint-details-form-row">
                  <div className="complaint-details-form-label">
                    Description
                  </div>
                  <div className="complaint-details-form-value">
                    {complaint.description}
                  </div>
                </div>

                <div className="complaint-details-form-row">
                  <div className="complaint-details-form-label">Notes</div>
                  <div className="complaint-details-form-value">
                    {(() => {
                      try {
                        const notes = JSON.parse(complaint.notes || '[]');
                        if (Array.isArray(notes) && notes.length > 0) {
                          // Reverse the order to show most recent notes first
                          const reversedNotes = [...notes].reverse();
                          return reversedNotes.map((note, index) => (
                            <div
                              key={index}
                              style={{
                                marginBottom: '10px',
                                paddingBottom: '10px',
                                borderBottom:
                                  index < reversedNotes.length - 1
                                    ? '1px solid #e0e0e0'
                                    : 'none',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: '#666',
                                  marginBottom: '4px',
                                }}
                              >
                                {(() => {
                                  // Handle both old ISO format and new local format
                                  if (note.timestamp.includes('T')) {
                                    // Old ISO format - convert to local time
                                    return new Date(
                                      note.timestamp,
                                    ).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit',
                                      hour12: true,
                                    });
                                  } else {
                                    // New local format - display as is
                                    return note.timestamp;
                                  }
                                })()}{' '}
                                - {note.user}
                                {note.type === 'closing_comment' && (
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      color: '#dc3545',
                                    }}
                                  >
                                    (Closing Comment)
                                  </span>
                                )}
                                {note.type === 'note' && (
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      color: '#007bff',
                                    }}
                                  >
                                    (Note)
                                  </span>
                                )}
                                {note.type === 'general_note' && (
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      color: '#007bff',
                                    }}
                                  >
                                    (Note)
                                  </span>
                                )}
                                {note.type === 'status_change' && (
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      color: '#28a745',
                                    }}
                                  >
                                    (Status Change)
                                  </span>
                                )}
                                {note.type === 'status_update' && (
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      color: '#28a745',
                                    }}
                                  >
                                    (Status Update)
                                  </span>
                                )}
                              </div>
                              <div>{note.comment}</div>
                            </div>
                          ));
                        }
                        return (
                          <div style={{ color: '#999', fontStyle: 'italic' }}>
                            No notes available
                          </div>
                        );
                      } catch (e) {
                        return (
                          <div style={{ color: '#999', fontStyle: 'italic' }}>
                            No notes available
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Horizontal Progress Timeline at Bottom */}
          <div className="rt-section" style={{ marginBottom: '0px' }}>
            <h3
              className="rt-section-title"
              style={{ marginBottom: '15px', paddingBottom: '0px' }}
            >
              Progress Timeline
            </h3>
            <div className="progress-timeline">
              {getProgressSteps().map((step, index) => (
                <div
                  key={step.status}
                  className={`progress-step ${step.isActive ? 'active' : ''} ${step.isCurrent ? 'current' : ''}`}
                >
                  {/* Current Status indicator */}
                  {step.isCurrent && (
                    <div className="current-status-indicator">
                      Current Status
                    </div>
                  )}

                  {/* Spacer for non-current steps to maintain alignment */}
                  {!step.isCurrent && (
                    <div
                      className="spacer"
                      style={{
                        height: '10px',
                        marginBottom: '0px',
                      }}
                    />
                  )}

                  {/* Horizontal connecting line */}
                  {index < getProgressSteps().length - 1 &&
                    step.isActive &&
                    !step.isCurrent && (
                      <div className="progress-connecting-line" />
                    )}

                  {/* Status icon */}
                  <div
                    className={`progress-icon ${step.isActive ? 'active' : 'inactive'}`}
                  >
                    {step.isActive ? '✓' : getStatusIcon(step.status)}
                  </div>

                  {/* Status info */}
                  <div className="progress-status-info">
                    <div
                      className={`progress-status-label ${step.isCurrent ? 'current' : ''} ${step.isActive ? 'active' : ''}`}
                      style={{
                        fontWeight: step.isCurrent ? 'bold' : 'normal',
                        color: step.isActive ? '#007bff' : '#6c757d',
                      }}
                    >
                      {step.label}
                    </div>
                    <div className="progress-status-description">
                      {step.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </RTPageLayout>

      {!readOnly && (
        <>
          {/* Unified Update Complaint Status Modal */}
          <UpdateComplaintStatusModal
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setUpdateAction(null);
            }}
            complaint={complaint}
            action={updateAction}
            onSuccess={fetchComplaintDetails}
          />

          {/* Assign Complaint Modal */}
          {showAssignModal && (
            <AssignComplaintModal
              isOpen={showAssignModal}
              onClose={() => {
                setShowAssignModal(false);
                setAssignMode(null);
              }}
              complaint={complaint}
              mode={assignMode}
              onSuccess={() => {
                setShowAssignModal(false);
                setAssignMode(null);
                fetchComplaintDetails();
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default ViewComplaintDetails;
